import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { FileText, Loader2, Save, Rocket, History } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin editor for versioned legal documents.
 * - Lists all slugs with their version history.
 * - Saving a draft creates a new version (publishable flag).
 * - Publishing a new version bumps the effective document and triggers
 *   re-acceptance for every member on next login.
 */
export default function LegalAdminEditor() {
  const { getAuthHeader } = useAuth();
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/legal`, { headers: getAuthHeader() });
      setDocs(res.data.documents || []);
      if (!selected && res.data.documents?.length) {
        pickDoc(res.data.documents[0]);
      }
    } catch (err) {
      toast.error('Could not load legal documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const pickDoc = (doc) => {
    setSelected(doc);
    const latest = doc.versions?.[0];
    setContent(latest?.content_md || '');
    setEffectiveDate(latest?.effective_date || new Date().toISOString().slice(0, 10));
  };

  const save = async (publish) => {
    if (!selected) return;
    if (!content.trim()) { toast.error('Content is required'); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/legal/${selected.slug}`,
        { content_md: content, effective_date: effectiveDate, publish },
        { headers: getAuthHeader() }
      );
      toast.success(publish ? 'Published new version — members will re-accept.' : 'Draft saved');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !docs.length) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading legal docs…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="legal-admin-editor">
      <div>
        <h2 className="text-2xl font-playfair font-bold text-foreground mb-2 flex items-center gap-2">
          <FileText className="w-6 h-6 text-gold" /> Legal Documents
        </h2>
        <p className="text-muted-foreground text-sm">
          Edit and publish versioned Terms, Privacy, Disclaimers and other compliance documents.
          Publishing a new version will trigger the re-acceptance modal for every member on their next login.
        </p>
      </div>

      <div className="grid md:grid-cols-[260px_1fr] gap-6">
        {/* Left rail — slug list */}
        <div className="wm-card p-3 rounded-sm border border-border h-fit">
          {docs.map(d => {
            const isActive = selected?.slug === d.slug;
            const pubVersion = d.latest_published?.version;
            return (
              <button
                key={d.slug}
                onClick={() => pickDoc(d)}
                data-testid={`legal-doc-tab-${d.slug}`}
                className={`w-full text-left px-3 py-2 rounded-sm text-sm transition-colors ${
                  isActive ? 'bg-gold/15 text-foreground' : 'hover:bg-accent/50 text-muted-foreground'
                }`}
              >
                <div className="font-semibold">{d.title}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
                  {pubVersion ? `Live v${pubVersion}` : 'Not published'}
                  {d.versions?.length > 0 && ` · ${d.versions.length} version${d.versions.length > 1 ? 's' : ''}`}
                </div>
              </button>
            );
          })}
        </div>

        {/* Editor */}
        {selected && (
          <div className="wm-card p-6 rounded-sm border border-border space-y-4">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <h3 className="text-xl font-playfair font-bold text-foreground">{selected.title}</h3>
                <p className="text-xs text-muted-foreground">
                  Slug: <code className="text-gold">{selected.slug}</code> · Markdown supported
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => save(false)}
                  disabled={saving}
                  data-testid="save-draft-btn"
                >
                  <Save className="w-4 h-4 mr-2" /> Save draft
                </Button>
                <Button
                  onClick={() => save(true)}
                  disabled={saving}
                  className="bg-gold hover:bg-gold/90 text-black"
                  data-testid="publish-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
                  Publish new version
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-foreground">Effective Date</Label>
              <Input
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                className="mt-1 bg-input border-border max-w-xs"
                data-testid="effective-date-input"
              />
            </div>

            <div>
              <Label className="text-foreground">Content (Markdown)</Label>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="mt-1 bg-input border-border font-mono text-xs min-h-[420px]"
                data-testid="content-textarea"
              />
            </div>

            {selected.versions?.length > 1 && (
              <div>
                <h4 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-2">
                  <History className="w-4 h-4" /> Version history
                </h4>
                <div className="space-y-1 text-xs">
                  {selected.versions.map((v, idx) => (
                    <div
                      key={v.version}
                      className="flex items-center justify-between px-3 py-2 border border-border rounded-sm"
                    >
                      <span>
                        v{v.version} · Effective {v.effective_date}{' '}
                        {idx === 0 && <span className="text-gold">(current draft)</span>}
                      </span>
                      <span className={`text-[10px] uppercase tracking-wider ${v.is_published ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                        {v.is_published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
